import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import mixpanel from 'mixpanel-browser';
import { Page, Stack, Button, Pill, Modal, Loader } from '@nearst/ui';

import { useShop } from '@services/ShopProvider';
import { useCheckPlan } from '@components/UpgradeRequired';
import { registerInterest } from '@services/channels';

import ChannelUpgradeModal from '@components/Modals/Upgrade/ChannelUpgradeModal';
import ChannelHeader from '../../components/ChannelHeader';
import useChannelInfo from '../useChannelInfo';

import styles from './ChannelDetails.module.scss';

const ChannelEnableButton = ({ channel, enabled, shop }) => {
	const requiredPlans = channel.requirements?.plans || [];
	const { unlocked, upgrade, showDialog, setShowDialog } = useCheckPlan(requiredPlans, 'Channel card');

	const isAvailable = shop.availableChannels?.[channel.id];
	const url = channel.manageUrl?.replace('{shopId}', shop.id);

	if (!channel.manageUrl) return null;

	if (!isAvailable && !enabled) return <p>Not available for your region or plan type.</p>;

	if (!unlocked && !enabled) {
		return (
			<>
				<ChannelUpgradeModal showDialog={showDialog} setShowDialog={setShowDialog} channel={channel} />
				<Button className={styles.ctaButton} onClick={upgrade} primary>
					Upgrade
				</Button>
			</>
		);
	}

	return (
		<Button data-cy="channel-enable-button" className={styles.ctaButton} primary as={Link} to={url}>
			{enabled ? 'Manage connection' : 'Enable'}
		</Button>
	);
};

const RegisterInterestButton = ({ channel, shop, status }) => {
	const alreadyRegistered = status === 'pending' || status === 'enabled';
	const [showModal, setShowModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const [submitted, setSubmitted] = useState(alreadyRegistered);
	const [error, setError] = useState(false);

	const handleRegisterInterest = async () => {
		mixpanel.track(`Register interest click - ${channel.title || channel.id}`);

		if (channel.registerInterestUrl) {
			window.open(`${channel.registerInterestUrl}?organisation_id=${shop.organisationId}`, '_blank');
			return;
		}

		setLoading(true);
		setError(false);
		try {
			await registerInterest(shop.id, channel.id);
			setSubmitted(true);
			setShowModal(true);
		} catch (e) {
			console.error(e);
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<Button className={styles.ctaButton} onClick={handleRegisterInterest} disabled={loading || submitted} primary>
				{submitted ? 'Interest registered' : 'Register interest'}
				{loading && <Loader size={14} />}
			</Button>
			{submitted && !error && (
				<p className={styles.helperText}>Our team will be in touch to help get you set up.</p>
			)}
			{error && <p className={styles.error}>Something went wrong. Please try again or email support@near.st</p>}
			<Modal isOpen={showModal} onDismiss={() => setShowModal(false)} aria-label="Interest registered">
				<div className={styles.modalContent}>
					<CheckCircleIcon height={24} color="var(--green)" />
					<h3>Interest registered</h3>
					<p>
						We've registered your interest in {channel.title}. A member of our customer success team will be in touch.
					</p>
					<p>
						If you'd like to follow up, you can also email{' '}
						<a href="mailto:support@near.st">support@near.st</a>.
					</p>
					<Button onClick={() => setShowModal(false)} primary>
						Close
					</Button>
				</div>
			</Modal>
		</>
	);
};

const ChannelDetails = () => {
	const { shop } = useShop();

	const { channelId, channelDefinitions, channelDefinition, status } = useChannelInfo();

	if (!channelDefinition) {
		return <Navigate to={`/channels/${shop.id}`} />;
	}

	return (
		<Stack space="2rem">
			<ChannelHeader channelId={channelId} status={status} />
			<Page.Section sidebarLayout>
				<Page.Section.Sidebar>
					<ul className={styles.featureList}>
						{channelDefinition.registerInterestUrl || channelDefinition.comingSoon ? (
							<RegisterInterestButton channel={channelDefinition} shop={shop} status={status} />
						) : (
							<ChannelEnableButton channel={channelDefinition} enabled={status === 'enabled'} shop={shop} />
						)}
						{channelDefinition.pricing && (
							<li>
								<h4>Pricing</h4>
								<p>{channelDefinition.pricing}</p>
							</li>
						)}
						{channelDefinition.category && (
							<li>
								<h4>Category</h4>
								<span>
									<Tippy content={channelDefinition.categorySubtitle}>
										<span>
											<Pill colour="grey">{channelDefinition.category}</Pill>
										</span>
									</Tippy>
								</span>
							</li>
						)}
						{channelDefinition.worksWith && channelDefinition.worksWith.length > 0 && (
							<li>
								<h4>Works with</h4>
								<ul>
									{channelDefinition.worksWith.map((channelId) => {
										const linkedChannel = channelDefinitions[channelId];
										if (!linkedChannel) return null;
										return (
											<li key={channelId}>
												<Link className={styles.link} to={`/channels/${shop.id}/details/${channelId}`}>
													{linkedChannel.title}
												</Link>
											</li>
										);
									})}
								</ul>
							</li>
						)}
						{channelDefinition.helpArticleUrls && (
							<li>
								<h4>Support</h4>
								<ul>
									{channelDefinition.helpArticleUrls.map((link) => (
										<li key={link.url}>
											<Link className={styles.link} to={link.url} target="_blank" rel="noreferrer">
												{link.title}
											</Link>
										</li>
									))}
								</ul>
							</li>
						)}
					</ul>
				</Page.Section.Sidebar>
				<Page.Section.Content>
					{channelDefinition.coverImageUrl && (
						<img className={styles.coverImage} src={channelDefinition.coverImageUrl} alt="Cover" />
					)}
					<div className={styles.longDescription} dangerouslySetInnerHTML={{ __html: channelDefinition.longDescriptionHtml }} />
				</Page.Section.Content>
			</Page.Section>
		</Stack>
	);
};

export default ChannelDetails;
